/* global--- fetch localStorage*/
import "whatwg-fetch"

import { createContext } from "react"

class Api {
  constructor(baseURL) {
    this.baseURL =
      baseURL ||
      process.env.REACT_APP_API_BASE_URL ||
      (process.env.REACT_APP_DEPLOY_ENV === "production"
        ? "https://api.savvifi.com/v1/"
        : "https://api.savvifi.dev/v1/")
    this.state = {}
  }

  formatUrl(endpoint, params) {
    let destination
    let remote = endpoint.startsWith("http")
      ? endpoint
      : this.baseURL + endpoint
    if (Object.keys(params).length === 0) {
      destination = `${remote}`
    } else {
      let queries = Object.keys(params).map(key => {
        let value = params[key]
        if (Object.prototype.toString.call(value) === "object Array") {
          value = value.join(",")
        }
        if (value === null) {
          return `${key}`
        }
        value = encodeURIComponent(value)
        return `${key}=${value}`
      })

      let formattedParams = queries.reduce((a, b) => {
        return `${a}&${b}`
      })
      destination = `${remote}?${formattedParams}`
    }
    return destination
  }

  fetch = async (endpoint, query, method, body) => {
    const destination = this.formatUrl(endpoint, query)
    if (typeof body !== "object") {
      throw new Error("body must be object")
    }
    const authHeaders = JSON.parse(localStorage.getItem("authHeaders"))
    if (!authHeaders && !destination.includes("/auth/")) {
      throw new Error("authorization required")
    }

    const headers = Object.assign(
      {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
        "X-Request-Id":
          Math.round(new Date().getTime() / 1000).toString(16) +
          "-" +
          Math.round(Math.random() * 36 ** 12).toString(36) +
          Math.round(Math.random() * 36 ** 12).toString(36)
      },
      authHeaders
    )
    if (headers.hasOwnProperty("Authorization") && !!headers["Authorization"]) {
      delete headers.Authorization
    }

    const options = {
      method,
      headers,
      credentials: "include"
    }
    if (method.toLowerCase() !== "get") {
      options.body = JSON.stringify(body)
    }

    return fetch(destination, options)
  }

  fetchText = async (endpoint, query, method, body) => {
    let response = null
    try {
      response = await this.fetch(endpoint, query, method, body)
    } catch (error) {
      throw new Error("Network Error")
    }
    const text = await response.text()
    if (response.status >= 200 && response.status <= 300) {
      return text
    }
    throw new Error(text || response.statusText)
  }

  fetchJson = async (endpoint, query, method, body) => {
    let response = null
    try {
      response = await this.fetch(endpoint, query, method, body)
    } catch (error) {
      throw new Error("Network Error")
    }
    const data = await response.json()
    if (response.status >= 200 && response.status <= 300) {
      return data
    }
    if (response.status > 401 && response.status < 500) {
      throw new ApiException(response.status, response.statusText)
    }
    if (data && data.error) {
      throw new Error(data.error.message)
    }
    throw new Error(response.statusText)
  }

  setBearerToken = token => {
    let headers = JSON.parse(localStorage.getItem("authHeaders")) || {}
    if (!!process.env.REACT_APP_DISABLE_COOKIES) {
      Object.assign(headers, {
        Authorization: `Bearer ${token}`
      })
    } else {
      Object.assign(headers, {
        "X-CSRF-Token": token
      })
    }
    localStorage.setItem("authHeaders", JSON.stringify(headers))
  }

  setSessionData = (expires_at, renew_by) => {
    let sessionData = this.getSessionData()
    Object.assign(sessionData, {
      renewed_at: new Date(),
      expires_at,
      renew_by
    })
    localStorage.setItem("sessionData", JSON.stringify(sessionData))
  }

  revokeAuth = () => {
    localStorage.removeItem("authHeaders")
    localStorage.removeItem("sessionData")
    return true
  }

  hasAuthToken = () => {
    // return true if user might be authorized
    return (
      localStorage.getItem("authHeaders") != null &&
      localStorage.getItem("authHeaders") !== "{}"
    )
  }

  getSessionData = () => JSON.parse(localStorage.getItem("sessionData")) || {}

  get = (endpoint = "", query = {}) =>
    this.fetchJson(endpoint, query, "get", {})

  post = (endpoint = "", query = {}, body = {}) =>
    this.fetchJson(endpoint, query, "post", body)

  patch = (endpoint = "", query = {}, body = {}) =>
    this.fetchJson(endpoint, query, "PATCH", body)

  put = (endpoint = "", query = {}, body = {}) =>
    this.fetchJson(endpoint, query, "PUT", body)

  delete = (endpoint = "", query = {}) =>
    this.fetchJson(endpoint, query, "delete", {})

  setUserPersistantData = (user_id, key, value = {}) => {
    const userPersistantData = JSON.parse(
      localStorage.getItem("userData") || "{}"
    )
    const newData = Object.assign({}, userPersistantData, {
      [user_id]: Object.assign({}, userPersistantData[user_id] || {}, {
        [key]: value
      })
    })
    localStorage.setItem("userData", JSON.stringify(newData))
  }

  getUserPersistantData = (user_id, key) => {
    const userPersistantData = JSON.parse(
      localStorage.getItem("userData") || "{}"
    )
    return userPersistantData[user_id] && userPersistantData[user_id][key]
  }
}

class ApiException {
  constructor(status, message) {
    this.error = true
    this.status = status
    this.message = message
  }
}

const api = new Api()

export const apiContext = createContext(api)

export default api
